/**
 * Task List Manager — pick a product + review mode and edit the task
 * checklist for that combination. Recreated from digital_sizer's
 * taskLists/TaskListManager.tsx (TS/Tailwind/lucide → JS/MUI), wired onto
 * eastviewos's backend via useTaskListsHook.
 *
 *   - Top selectors for productType + reviewType (Closing is one of the
 *     reviewType options)
 *   - AG-Grid Enterprise table with multi-line editable taskText, sortable /
 *     filterable / resizable / drag-reorderable columns
 *   - Row-level edits PATCH the task and roll into the audit trail
 *   - "Details" opens a side drawer with the riskBeingMitigated editor,
 *     related docs / tasks pickers, and condition editor
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { AllEnterpriseModule, ModuleRegistry } from "ag-grid-enterprise";
import CheckBoxOutlined from "@mui/icons-material/CheckBoxOutlined";
import OpenInNewOutlined from "@mui/icons-material/OpenInNewOutlined";
import RefreshOutlined from "@mui/icons-material/RefreshOutlined";
import { Box, CircularProgress, Typography } from "@mui/material";

import AgGridTableWrapper from "../../components/agGrid/AgGridTableWrapper";
import SearchInput from "../../components/inputs/SearchInput";
import { useTaskListsHook } from "../../hooks/useTaskListsHook";
import TaskDrilldownDrawer from "./TaskDrilldownDrawer";

// Enterprise features used below (agSetColumnFilter, sideBar, agRichSelect).
ModuleRegistry.registerModules([AllEnterpriseModule]);

// eslint-disable-next-line no-unused-vars
const REVIEW_KEY_BY_LABEL = {
  "Pre-Close FULL": "pre_close_full",
  "Pre-Close LIGHT": "pre_close_light",
  "Post-Close FULL": "post_close_full",
  "Post-Close LIGHT": "post_close_light",
  "Post-Close Super Light": "post_close_super_light",
  Closing: "closing",
};

// Per-product chip colors — each loan product gets its own hue (text + a
// subtle tinted background) so the applicability column is scannable.
const PRODUCT_CHIP_COLORS = {
  DSCR: { fg: "#1d4ed8", bg: "#eff6ff" }, // blue
  RTL: { fg: "#15803d", bg: "#f0fdf4" }, // green
  GUC: { fg: "#c2410c", bg: "#fff7ed" }, // orange
  MF: { fg: "#7e22ce", bg: "#faf5ff" }, // purple
};

const selectSx = {
  px: "8px",
  py: "4px",
  bgcolor: "#fff",
  border: "1px solid #d1d5db",
  borderRadius: "4px",
  fontSize: 12,
  color: "#0f172a",
  font: "inherit",
  cursor: "pointer",
  "&:focus": {
    outline: "none",
    boxShadow: "0 0 0 2px rgba(59,130,246,0.4)",
  },
};

export default function TaskListManager() {
  const gridRef = useRef(null);
  const { getTaskOptions, listTasks, updateTask } = useTaskListsHook();

  const [products, setProducts] = useState([]);
  const [reviewModes, setReviewModes] = useState([]);
  const [product, setProduct] = useState("");
  const [reviewMode, setReviewMode] = useState("");

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [activeTask, setActiveTask] = useState(null);
  const [search, setSearch] = useState("");

  // ── Load options once ───────────────────────────────────────────────────
  useEffect(() => {
    getTaskOptions()
      .then((opts) => {
        setProducts(opts.products || []);
        setReviewModes(opts.review_modes || []);
        if (opts.products?.length) setProduct(opts.products[0]);
        if (opts.review_modes?.length) setReviewMode(opts.review_modes[0]);
      })
      .catch((err) => setError(err?.message || "Failed to load options"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Load tasks for the current selection ────────────────────────────────
  // Empty product/reviewMode mean "All" — backend returns every task when
  // those query params are omitted.
  const loadTasks = useCallback(async () => {
    setError(null);
    try {
      const data = await listTasks(
        product || undefined,
        reviewMode || undefined,
      );
      setTasks(data);
    } catch (err) {
      setError(
        err?.response?.data?.detail || err?.message || "Failed to load tasks",
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, reviewMode]);

  useEffect(() => {
    setLoading(true);
    loadTasks().finally(() => setLoading(false));
  }, [loadTasks]);

  const refresh = async () => {
    setRefreshing(true);
    await loadTasks();
    setRefreshing(false);
  };

  // ── Row PATCH helper ────────────────────────────────────────────────────
  const patchRow = useCallback(
    async (taskId, patch) => {
      try {
        const updated = await updateTask(taskId, patch);
        setTasks((prev) => prev.map((t) => (t.id === taskId ? updated : t)));
      } catch (err) {
        const detail = err?.response?.data?.detail;
        let msg;
        if (Array.isArray(detail)) {
          msg = detail
            .map(
              (d) =>
                `${(d.loc || []).join(".")}: ${d.msg || JSON.stringify(d)}`,
            )
            .join("\n");
        } else if (typeof detail === "string") {
          msg = detail;
        } else {
          msg = err?.message || "Save failed";
        }
        alert(`Save failed:\n${msg}`);
        loadTasks();
      }
    },
    [loadTasks, updateTask],
  );

  // ── Columns ─────────────────────────────────────────────────────────────
  const columns = useMemo(
    () => [
      {
        field: "orderOfReview",
        headerName: "#",
        editable: true,
        width: 70,
        filter: "agNumberColumnFilter",
        // Keep the floating-filter input, drop its menu (funnel) button.
        floatingFilterComponentParams: { suppressFilterButton: true },
      },
      {
        field: "sourceDocument",
        headerName: "Source Document",
        editable: true,
        filter: "agSetColumnFilter",
        minWidth: 180,
        flex: 0.7,
      },
      {
        field: "taskText",
        headerName: "Task",
        editable: true,
        cellEditor: "agLargeTextCellEditor",
        cellEditorPopup: true,
        cellEditorParams: { maxLength: 4000, rows: 6, cols: 70 },
        filter: "agTextColumnFilter",
        autoHeight: true,
        wrapText: true,
        cellStyle: {
          whiteSpace: "normal",
          lineHeight: "1.4",
          paddingTop: "6px",
          paddingBottom: "6px",
        },
        minWidth: 320,
        flex: 2.2,
      },
      {
        field: "riskBeingMitigated",
        headerName: "Risk Mitigated",
        editable: true,
        cellEditor: "agLargeTextCellEditor",
        cellEditorPopup: true,
        cellEditorParams: { maxLength: 1000, rows: 4, cols: 60 },
        filter: "agTextColumnFilter",
        cellRenderer: (p) =>
          p.value ? (
            <Box
              component="span"
              sx={{ fontSize: 12, color: "#334155", lineHeight: 1.4 }}
            >
              {p.value}
            </Box>
          ) : (
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                px: "8px",
                py: "2px",
                borderRadius: 999,
                color: "#b45309",
                bgcolor: "#fffbeb",
                border: "1px solid #fde68a",
              }}
            >
              <Box
                component="span"
                sx={{
                  width: 5,
                  height: 5,
                  borderRadius: "50%",
                  bgcolor: "#f59e0b",
                }}
              />
              Required
            </Box>
          ),
        autoHeight: true,
        wrapText: true,
        cellStyle: {
          whiteSpace: "normal",
          lineHeight: "1.4",
          paddingTop: "6px",
          paddingBottom: "6px",
        },
        minWidth: 220,
        width: 240,
        flex: 1.3,
      },
      {
        field: "loanLevelConditionPolicy",
        headerName: "Condition Policy",
        editable: true,
        filter: "agTextColumnFilter",
        minWidth: 220,
        flex: 1.2,
      },
      {
        field: "authorityToWaive",
        headerName: "Authority to Waive",
        editable: true,
        filter: "agSetColumnFilter",
        minWidth: 160,
      },
      {
        field: "clearingParty",
        headerName: "Clearing Party",
        editable: true,
        cellEditor: "agRichSelectCellEditor",
        cellEditorParams: { values: ["", "Internal", "External"] },
        filter: "agSetColumnFilter",
        width: 130,
      },
      {
        headerName: "Products",
        editable: false,
        sortable: false,
        filter: false,
        cellRenderer: (p) => {
          const pa = p.data?.productApplicability;
          if (!pa) return null;
          const on = ["DSCR", "RTL", "GUC", "MF"].filter((k) => pa[k]);
          if (!on.length)
            return (
              <Box component="span" sx={{ fontSize: 12, color: "#cbd5e1" }}>
                —
              </Box>
            );
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                height: "100%",
                gap: "4px",
                whiteSpace: "nowrap",
              }}
            >
              {on.map((k) => {
                const c = PRODUCT_CHIP_COLORS[k];
                return (
                  <Box
                    component="span"
                    key={k}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      height: 20,
                      px: "6px",
                      fontSize: 10,
                      fontWeight: 700,
                      lineHeight: 1,
                      color: c.fg,
                      bgcolor: c.bg,
                      borderRadius: "4px",
                    }}
                  >
                    {k}
                  </Box>
                );
              })}
            </Box>
          );
        },
        width: 188,
      },
      {
        field: "reviewer",
        headerName: "Reviewer",
        editable: true,
        filter: "agSetColumnFilter",
        width: 140,
      },
      {
        field: "last_edited_by",
        headerName: "Last Edited By",
        editable: false,
        filter: "agTextColumnFilter",
        width: 180,
      },
      {
        headerName: "",
        field: "id",
        pinned: "right",
        editable: false,
        sortable: false,
        filter: false,
        suppressMovable: true,
        width: 120,
        cellStyle: { display: "flex", alignItems: "center" },
        cellRenderer: (p) => (
          <Box
            component="button"
            onClick={() => p.data && setActiveTask(p.data)}
            title="Open task drilldown"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.625,
              px: 1.25,
              py: 0.5,
              fontSize: 12,
              fontWeight: 600,
              color: "#1d4ed8",
              bgcolor: "#fff",
              border: "1px solid #bfdbfe",
              borderRadius: "6px",
              cursor: "pointer",
              transition: "all 120ms ease",
              "&:hover": {
                color: "#1e3a8a",
                bgcolor: "#eff6ff",
                borderColor: "#93c5fd",
              },
            }}
          >
            <OpenInNewOutlined sx={{ fontSize: 13 }} />
            Details
          </Box>
        ),
      },
    ],
    [],
  );

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      sortable: true,
      filter: true,
      floatingFilter: true,
      suppressMovable: false,
      cellClass: "ag-cell-vertical-center",
      suppressHeaderMenuButton: true,
      suppressHeaderContextMenu: true,
    }),
    [],
  );

  const onCellValueChanged = async (evt) => {
    if (!evt.data || !evt.colDef.field) return;
    const field = evt.colDef.field;
    const patch = { [field]: evt.data[field] };
    await patchRow(evt.data.id, patch);
  };

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        bgcolor: "#fff",
      }}
    >
      {/* Header + selectors */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 1.5,
          px: 2.5,
          py: 1.5,
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mr: 1 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#0f172a" }}>
            Task List Manager
          </Typography>
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Search all tasks..."
            size="small"
            rounded
            sx={{ width: 280 }}
          />
        </Box>

        <Box
          component="label"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            fontSize: 12,
            color: "#64748b",
          }}
        >
          Product
          <Box
            component="select"
            value={product}
            onChange={(e) => setProduct(e.target.value)}
            sx={selectSx}
          >
            <option value="">All products</option>
            {products.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </Box>
        </Box>

        <Box
          component="label"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            ml: 1.5,
            fontSize: 12,
            color: "#64748b",
          }}
        >
          Review type
          <Box
            component="select"
            value={reviewMode}
            onChange={(e) => setReviewMode(e.target.value)}
            sx={selectSx}
          >
            <option value="">All review types</option>
            {reviewModes.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </Box>
        </Box>

        <Typography sx={{ fontSize: 11, color: "#64748b", ml: "auto" }}>
          {tasks.length} task{tasks.length === 1 ? "" : "s"}
        </Typography>
        <Box
          component="button"
          onClick={refresh}
          disabled={refreshing}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            px: 1.25,
            py: 0.75,
            fontSize: 12,
            color: "#334155",
            bgcolor: "transparent",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            "&:hover": { color: "#0f172a", bgcolor: "#e5e7eb" },
            "&:disabled": { opacity: 0.4, cursor: "default" },
          }}
        >
          {refreshing ? (
            <CircularProgress size={12} sx={{ color: "#334155" }} />
          ) : (
            <RefreshOutlined sx={{ fontSize: 12 }} />
          )}
          Refresh
        </Box>
      </Box>

      {error && (
        <Box
          sx={{
            px: 2.5,
            py: 1,
            bgcolor: "#fef2f2",
            color: "#b91c1c",
            fontSize: 14,
            borderBottom: "1px solid #fecaca",
            whiteSpace: "pre-wrap",
          }}
        >
          {error}
        </Box>
      )}

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {loading ? (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#64748b",
              gap: 1,
            }}
          >
            <CircularProgress size={18} />
            Loading tasks…
          </Box>
        ) : (
          <AgGridTableWrapper
            fullHeight
            noBorder
            noBorderRadius
            compactTable
            sx={{ flex: 1, minHeight: 0 }}
          >
            <AgGridReact
              ref={gridRef}
              rowData={tasks}
              columnDefs={columns}
              defaultColDef={defaultColDef}
              quickFilterText={search}
              onCellValueChanged={onCellValueChanged}
              onRowDoubleClicked={(evt) => evt.data && setActiveTask(evt.data)}
              animateRows
              singleClickEdit
              stopEditingWhenCellsLoseFocus
              rowSelection="multiple"
              headerHeight={54}
              floatingFiltersHeight={40}
              sideBar={{
                toolPanels: [
                  {
                    id: "columns",
                    labelDefault: "Columns",
                    labelKey: "columns",
                    iconKey: "columns",
                    toolPanel: "agColumnsToolPanel",
                  },
                  {
                    id: "filters",
                    labelDefault: "Filters",
                    labelKey: "filters",
                    iconKey: "filter",
                    toolPanel: "agFiltersToolPanel",
                  },
                ],
              }}
              getRowId={(p) => p.data.id}
            />
          </AgGridTableWrapper>
        )}
      </Box>

      <TaskDrilldownDrawer
        task={activeTask}
        allTasks={tasks}
        onClose={() => setActiveTask(null)}
        onSave={async (patch) => {
          if (!activeTask) return;
          await patchRow(activeTask.id, patch);
          setActiveTask((prev) => (prev ? { ...prev, ...patch } : prev));
        }}
      />
    </Box>
  );
}
